import {
  createContext,
  Suspense,
  useContext,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useScrollToHash } from './useScrollToHash';
import { Box, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { t } from 'i18next';
import { ErrorBoundary } from 'react-error-boundary';

import type {
  TGetDashboardByIdResponse,
  TGetWidgetDataByIdParams,
} from '../../utility/types/dataProvider';
import SkeletonBox from '../../components/generic/SkeletonBox';
import DashboardError from './DashboardError';
import { dashboardTitleHeight } from './constants';
import DashboardLayout from './DashboardLayout';
import { AssetViewContextType } from 'screens/editorial/asset/context/AssetViewContextProvider';
import WidgetMask from 'components/Dashboard/components/WidgetMask';
import DashboardHeader from 'components/Dashboard/components/DashboardHeader';
import { useDashboard } from './hooks/useDashboard';
import DashboardNoAccess from './components/DashboardNoAccess';
import useDashboardPermissions from './hooks/useDashboardPermissions';

export type TDashboardFilters = TGetWidgetDataByIdParams['query'];

export type TDashboardUserPermissions = {
  create: boolean;
  edit: boolean;
  delete: boolean;
  share: boolean;
  full: boolean;
};

type TDashboardProps = {
  app: string;
  assetUUID?: string;
  columns?: string[] | undefined;
  hasEqualHeightColumns?: boolean;
  isChild?: boolean;
  isFullHeight?: boolean;
  // For editorial asset dashboards, to help form the side navigation.
  setStatsMenuListWidgetIds?: AssetViewContextType['setStatsMenuListWidgetIds'];
  filters?: TDashboardFilters;
  setDashboardFilters?: Dispatch<SetStateAction<TDashboardFilters | undefined>>;
  forceBeta?: boolean;
  isEditable?: boolean;
};

export type TDashboardContext = TDashboardProps & {
  appId: string;
  appLocation: string;
  columns: string[] | undefined;
  dashboard: TGetDashboardByIdResponse['data'] | undefined;
  isAssetDashboard: boolean;
  filters: TDashboardFilters;
  filtersReady: boolean;
  isDashboardMasked: boolean;
  hasProDataInsights: boolean;
  isLoading: boolean;
  initDashboardFiltersRef: React.MutableRefObject<TDashboardFilters | null>;
  isEditable?: boolean;
  permissions?: TDashboardUserPermissions;
};

export const DashboardContext = createContext<TDashboardContext>({
  app: 'core',
  appId: 'core.home',
  appLocation: 'home',
  assetUUID: undefined,
  columns: undefined,
  dashboard: undefined,
  hasEqualHeightColumns: false,
  hasProDataInsights: false,
  isAssetDashboard: false,
  isChild: false,
  isFullHeight: true,
  filters: {},
  filtersReady: false,
  isDashboardMasked: false,
  isLoading: false,
  initDashboardFiltersRef: { current: null },
  isEditable: false,
});

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboardContext must be used within DashboardContext!');
  }
  return context;
};

const Dashboard = ({
  app,
  assetUUID,
  hasEqualHeightColumns = false,
  isFullHeight = true,
  isChild = false,
  setStatsMenuListWidgetIds,
  isEditable = false,
  filters: filtersProp,
  forceBeta = false,
}: TDashboardProps) => {
  // Scroll to the widget specified in the URL hash, if present (for bell notifications).
  useScrollToHash();

  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [appId, appLocation] = useMemo(() => app.split('.'), [app]);

  // Feeds dashboard is in a future sprint and not implemented yet, this should work once it is but something to double-check.
  const isBeta = forceBeta || app === 'feeds.home';

  const permissions = useDashboardPermissions();

  const {
    dashboard,
    dashboardFilters,
    setDashboardFilters: setDashboardFiltersInternal,
    columns,
    isDashboardMasked,
    lacksUserPermissions,
    areFiltersReady,
    isReadyToRender,
    isLoading,
    isError,
    error,
    hasProDataInsights,
    isAssetDashboard,
    isKpiBarDashboard,
    initDashboardFiltersRef,
  } = useDashboard({
    app,
    assetUUID,
    isChild,
    setStatsMenuListWidgetIds,
  });

  if (error) {
    return <DashboardError />;
  }

  if (lacksUserPermissions) {
    return <DashboardNoAccess />;
  }

  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <DashboardError
          error={error as Error}
          resetErrorBoundary={resetErrorBoundary}
        />
      )}
    >
      <Suspense
        fallback={
          <SkeletonBox
            text="Loading dashboard..."
            width="100%"
            height="100%"
          />
        }
      >
        <DashboardContext.Provider
          value={{
            app,
            appId,
            appLocation,
            assetUUID,
            columns,
            dashboard,
            hasEqualHeightColumns,
            hasProDataInsights,
            isAssetDashboard,
            isChild,
            isFullHeight,
            setDashboardFilters: setDashboardFiltersInternal,
            filters: isKpiBarDashboard ? filtersProp : dashboardFilters,
            filtersReady: areFiltersReady,
            isDashboardMasked,
            isLoading,
            initDashboardFiltersRef,
            isEditable,
            permissions,
          }}
        >
          {!isReadyToRender ? (
            <SkeletonBox
              text="Loading dashboard..."
              width="100%"
              height="100%"
            />
          ) : (
            <Box
              data-testid={`dashboard-${app}`}
              className={`dashboard dashboard-${app}`}
              sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: isChild
                  ? 'none'
                  : theme.palette.mode === 'light'
                    ? blueGrey[100]
                    : 'inherit',
                minHeight: isFullHeight || !isChild ? '100%' : 'auto',
                position: 'relative',
              })}
            >
              {isDashboardMasked && (
                <WidgetMask
                  isDashboard
                  title={t('blox.dashboards.mask.gen_title')}
                />
              )}
              {/* Dashboard Title. */}
              {isLoading && !isChild ? (
                <Skeleton
                  width={isMobileOrTablet ? '100%' : '30%'}
                  height={dashboardTitleHeight}
                  animation={false}
                />
              ) : isError ? null : (
                <DashboardHeader
                  isChild={isChild}
                  dashboardTitleHeight={dashboardTitleHeight}
                  isBeta={isBeta}
                  dashboard={dashboard}
                />
              )}

              {/* Columns Layout. */}
              {isLoading ? (
                <SkeletonBox
                  text=""
                  animation={false}
                  sx={{
                    minHeight: '100px',
                    flex: 1,
                  }}
                />
              ) : isError ? (
                <DashboardError />
              ) : (
                <DashboardLayout />
              )}
            </Box>
          )}
        </DashboardContext.Provider>
      </Suspense>
    </ErrorBoundary>
  );
};

export default Dashboard;
